// src/utils/email.js
// Utility functions for sending emails via Resend API

import { auth } from '../firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Send an email via the Resend API
 * @param {Object} emailData - Email data
 * @param {string|string[]} emailData.to - Recipient email(s)
 * @param {string} emailData.subject - Email subject
 * @param {string} emailData.html - HTML content (optional)
 * @param {string} emailData.text - Plain text content (optional)
 * @param {string} emailData.from - Sender email (optional, uses default)
 * @returns {Promise<Object>} Response from API
 */
// Helper function to detect development environment
const isDevelopmentEnvironment = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  const port = window.location.port;
  const isLocalNetwork = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(hostname);
  return hostname === 'localhost' || 
         hostname === '127.0.0.1' || 
         isLocalNetwork ||
         port === '3000' ||
         process.env.NODE_ENV === 'development';
};

/**
 * Base URL for the local Express API (server.js). Uses the current page hostname so
 * opening the app as http://192.168.x.x:3001 still calls http://192.168.x.x:4000, not localhost.
 */
function getDevelopmentApiOrigin() {
  if (typeof window === 'undefined') return 'http://localhost:4000';
  return `http://${window.location.hostname}:4000`;
}

function apiUnreachableDevHint() {
  return 'Could not reach the API on port 4000. Run `npm run start-server` in another terminal, or `npm run dev` to start both the app and API.';
}

export const sendEmail = async (emailData) => {
  try {
    // For Firebase App Hosting, API and frontend are on same domain - use relative URL
    // For development, API on same host as the SPA (port 4000)
    // For production, use full Cloud Functions URL if API_BASE_URL is not set
    const isDevelopment = isDevelopmentEnvironment();
    let emailApiUrl;
    
    // All Express routes have /api/ prefix, so we need /api/api/send-email for production
    if (API_BASE_URL) {
      // If API_BASE_URL is set, check if it ends with /api
      const baseUrlEndsWithApi = API_BASE_URL.endsWith('/api');
      emailApiUrl = baseUrlEndsWithApi 
        ? `${API_BASE_URL}/api/send-email`  // ...cloudfunctions.net/api/api/send-email
        : `${API_BASE_URL}/api/send-email`; // Custom domain: .../api/send-email
    } else if (isDevelopment) {
      emailApiUrl = `${getDevelopmentApiOrigin()}/api/send-email`;
    } else {
      // Production: Cloud Functions URL = ...cloudfunctions.net/api, Express route = /api/send-email
      // Full URL: ...cloudfunctions.net/api/api/send-email
      emailApiUrl = 'https://us-central1-epiphoric-prod.cloudfunctions.net/api/api/send-email';
    }
    
    const headers = {
      'Content-Type': 'application/json',
    };
    const user = auth.currentUser;
    if (user) {
      headers.Authorization = `Bearer ${await user.getIdToken()}`;
    }

    const response = await fetch(emailApiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(emailData),
    });

    console.log('[sendEmail] Response status:', response.status, response.statusText);

    const data = await response.json();

    if (!response.ok) {
      console.error('[sendEmail] Error response:', data);
      throw new Error(data.error || 'Failed to send email');
    }

    console.log('[sendEmail] Email sent successfully:', data);
    return data;
  } catch (error) {
    console.error('[sendEmail] Error sending email:', {
      error: error.message,
      stack: error.stack,
      emailData: { to: emailData.to, subject: emailData.subject }
    });
    throw error;
  }
};

/**
 * Send a welcome email to a new user
 * @param {string} to - Recipient email
 * @param {string} firstName - User's first name
 * @returns {Promise<Object>}
 */
export const sendWelcomeEmail = async (to, firstName = 'there') => {
  // Use production domain for email links, or fallback to current origin
  const appUrl = process.env.REACT_APP_FRONTEND_URL || 
                 process.env.REACT_APP_API_URL || 
                 (typeof window !== 'undefined' ? window.location.origin : 'https://epiphoric.com') ||
                 'https://epiphoric.com';
  
  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Welcome to Epiphoric</title>
      </head>
      <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center; border-radius: 8px 8px 0 0;">
          <h1 style="color: white; margin: 0; font-size: 28px;">Welcome to Epiphoric!</h1>
        </div>
        <div style="background: #f9fafb; padding: 30px; border-radius: 0 0 8px 8px; border: 1px solid #e5e7eb; border-top: none;">
          <p style="font-size: 16px; margin-top: 0;">Hi ${firstName},</p>
          <p style="font-size: 16px;">Welcome to Epiphoric, your high-velocity vault for capturing and tracking your creative ideas!</p>
          <p style="font-size: 16px;">We're excited to help you never let your genius fade. Start capturing your epiphanies and watch them grow into amazing projects.</p>
          <div style="margin: 30px 0; text-align: center;">
            <a href="${appUrl}" style="display: inline-block; background: #467096; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 600;">Get Started</a>
          </div>
          <p style="font-size: 14px; color: #6b7280; margin-top: 30px;">If you have any questions, feel free to reach out to us at <a href="mailto:cambriancreationlab@gmail.com" style="color: #467096;">cambriancreationlab@gmail.com</a></p>
        </div>
        <p style="text-align: center; color: #9ca3af; font-size: 12px; margin-top: 20px;">© ${new Date().getFullYear()} Epiphoric. All rights reserved.</p>
      </body>
    </html>
  `;

  const text = `
Welcome to Epiphoric!

Hi ${firstName},

Welcome to Epiphoric, your high-velocity vault for capturing and tracking your creative ideas!

We're excited to help you never let your genius fade. Start capturing your epiphanies and watch them grow into amazing projects.

Get started: ${appUrl}

If you have any questions, feel free to reach out to us at cambriancreationlab@gmail.com

© ${new Date().getFullYear()} Epiphoric. All rights reserved.
  `;

  return sendEmail({
    to,
    subject: 'Welcome to Epiphoric!',
    html,
    text,
  });
};

/**
 * Send an account deletion confirmation email
 * @param {string} to - Recipient email
 * @param {string} firstName - User's first name
 * @returns {Promise<Object>}
 */
export const sendAccountDeletionEmail = async (to, firstName = 'there') => {
  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Account Deletion Confirmation</title>
      </head>
      <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="background: #dc2626; padding: 30px; text-align: center; border-radius: 8px 8px 0 0;">
          <h1 style="color: white; margin: 0; font-size: 28px;">Account Deletion Confirmation</h1>
        </div>
        <div style="background: #f9fafb; padding: 30px; border-radius: 0 0 8px 8px; border: 1px solid #e5e7eb; border-top: none;">
          <p style="font-size: 16px; margin-top: 0;">Hi ${firstName},</p>
          <p style="font-size: 16px;">This email confirms that your Epiphoric account and all associated data have been permanently deleted.</p>
          <p style="font-size: 16px;">Your account, ideas, and all personal information have been completely removed from our systems.</p>
          <p style="font-size: 16px; margin-top: 30px;">Thank you for using Epiphoric.</p>
        </div>
        <p style="text-align: center; color: #9ca3af; font-size: 12px; margin-top: 20px;">© ${new Date().getFullYear()} Epiphoric. All rights reserved.</p>
      </body>
    </html>
  `;

  const text = `
Account Deleted

Hello,

This email confirms that your Epiphoric account and all associated data have been permanently deleted.

Your account, ideas, and all personal information have been completely removed from our systems.

Thank you for using Epiphoric.

© ${new Date().getFullYear()} Epiphoric. All rights reserved.
  `;

  return sendEmail({
    to,
    subject: 'Your Epiphoric Account Has Been Deleted',
    html,
    text,
  });
};

/**
 * Send email verification: Firebase action link from Admin SDK, delivered via Resend.
 * @returns {Promise<{ success: boolean, alreadyVerified?: boolean }>}
 */
export const requestEmailVerificationSend = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('You must be signed in to verify your email.');
  }
  const token = await user.getIdToken();

  const isDevelopment = isDevelopmentEnvironment();
  const apiUrl = API_BASE_URL
    ? `${API_BASE_URL}/api/email-verification-send`
    : isDevelopment
      ? `${getDevelopmentApiOrigin()}/api/email-verification-send`
      : '/api/email-verification-send';

  let response;
  try {
    response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({}),
    });
  } catch (e) {
    if (e?.message === 'Failed to fetch' || e?.name === 'TypeError') {
      throw new Error(apiUnreachableDevHint());
    }
    throw e;
  }

  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const text = await response.text();
    console.error('[requestEmailVerificationSend] Non-JSON response:', text.substring(0, 200));
    throw new Error('Could not send verification email. Check your API configuration.');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to send verification email');
  }

  return data;
};

/**
 * Request a password reset via Resend
 * @param {string} email - User's email address
 * @returns {Promise<Object>}
 */
export const requestPasswordReset = async (email) => {
  // For Firebase App Hosting, API and frontend are on same domain - use relative URL
  // For development, use localhost:4000
  // If API_BASE_URL is explicitly set, use it; otherwise use relative URL (same domain)
  
  const isDevelopment = isDevelopmentEnvironment();
  
  const apiUrl = API_BASE_URL 
    ? `${API_BASE_URL}/api/password-reset-request`
    : isDevelopment 
      ? `${getDevelopmentApiOrigin()}/api/password-reset-request`
      : '/api/password-reset-request'; // Relative URL for same-domain (App Hosting)
  
  try {
    let response;
    try {
      response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
    } catch (e) {
      if (e?.message === 'Failed to fetch' || e?.name === 'TypeError') {
        throw new Error(apiUnreachableDevHint());
      }
      throw e;
    }

    // Check if response is actually JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Non-JSON response received:', text.substring(0, 200));
      
      // Provide better error message based on the issue
      const isHtmlResponse = text.trim().toLowerCase().startsWith('<!doctype html') || text.trim().toLowerCase().startsWith('<html');
      if (isHtmlResponse) {
        throw new Error('API server is not configured. The API endpoint returned HTML instead of JSON. Please configure REACT_APP_API_URL in your production environment variables to point to your API server (e.g., Firebase Cloud Functions or a separate server).');
      }
      throw new Error('Server returned an invalid response. Please check your API server configuration.');
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to send password reset email');
    }

    return data;
  } catch (error) {
    console.error('Error requesting password reset:', error);
    throw error;
  }
};

